import { useLayoutEffect, useRef, useState } from "react";
import { useMediaUrl } from "./useMediaUrl";
import { computeGridMetrics, defaultRowLabels, shelfRect, GAP, type ShelfRegion } from "./shelfGrid";

/** 一个货架区域在网格上的视图模型 */
export interface ShelfView extends ShelfRegion {
  shelfId: string;
  /** 位置标识快照，如 "A3"（单格）或 "A1-B2"（合并区域） */
  code: string;
  /** 可选展示名，如 "新刊区" */
  label: string | null;
  iconUrl: string | null;
  /** 该货架内全部商品可用量为 0 */
  soldOut: boolean;
  productCount: number;
  /** 首图缩略图（按 sortOrder 第一个商品） */
  firstProductImage: string | null;
  /** manage 模式：待挑数量 */
  pendingPickQty?: number;
  /** 扫商品码/广告跳转时的定位高亮 */
  highlightProductId?: string | null;
}

export interface ShelfGridProps {
  rows: number;
  cols: number;
  /** 只传货架，不传空洞；空洞由组件按 rows×cols 推导 */
  shelves: ShelfView[];
  mode?: "browse" | "manage" | "pick";
  rowLabels?: string[];
  onSelectShelf?: (shelfId: string) => void;
  /** 合并选择模式下启用：点选格子而非货架 */
  mergeSelection?: {
    selected: Array<{ row: number; col: number }>;
    onToggleCell: (row: number, col: number) => void;
  };
  className?: string;
}

/**
 * 网格底上一个未分配的格子（空洞）——不可点击、不参与 Tab 顺序。
 *
 * `data-hole="row:col"` 是给测试留的抓手：空洞是「按 rows×cols 推导」而不是由
 * props 传进来的，它的数量与坐标最容易在重构里被悄悄改坏
 * （例如 left/top 漏掉 GAP，视觉上要等到格子挨在一起才看得出来）。
 */
function EmptyCell({
  row,
  col,
  left,
  top,
  size,
}: {
  row: number;
  col: number;
  left: number;
  top: number;
  size: number;
}) {
  return (
    <div
      aria-hidden="true"
      data-hole={`${row}:${col}`}
      className="absolute rounded-lg border-2 border-dashed border-gray-300 bg-gray-50"
      style={{ left, top, width: size, height: size }}
    />
  );
}

interface ShelfBlockProps {
  shelf: ShelfView;
  cellSize: number;
  mode: NonNullable<ShelfGridProps["mode"]>;
  onSelectShelf?: (shelfId: string) => void;
}

function ShelfBlock({ shelf, cellSize, mode, onSelectShelf }: ShelfBlockProps) {
  const imageUrl = useMediaUrl(shelf.iconUrl);
  const rect = shelfRect(shelf, cellSize);
  const clickable = mode === "browse" ? !shelf.soldOut : true;

  // 无图标时字号随区域高度分档（05-ui-design-system.md §4.5.1）
  const codeTextClass = rect.height < 56 ? "text-sm" : rect.height < 96 ? "text-base" : "text-lg";

  return (
    <button
      type="button"
      data-shelf-id={shelf.shelfId}
      data-shelf-code={shelf.code}
      disabled={!clickable}
      onClick={clickable ? () => onSelectShelf?.(shelf.shelfId) : undefined}
      className="absolute flex items-center justify-center overflow-hidden rounded-lg border-2 border-black bg-white font-bold active:bg-gray-100 disabled:cursor-not-allowed"
      style={{ left: rect.left, top: rect.top, width: rect.width, height: rect.height }}
    >
      {imageUrl ? (
        <img
          src={imageUrl}
          alt={shelf.label ?? shelf.code}
          className="absolute inset-0 h-full w-full object-cover"
          draggable={false}
        />
      ) : (
        <span className={codeTextClass}>{shelf.code}</span>
      )}

      {mode === "manage" && shelf.productCount > 0 && (
        <span className="absolute right-1 top-1 rounded bg-black/70 px-1.5 py-0.5 text-xs font-bold text-white">
          {shelf.productCount}
        </span>
      )}

      {/*
        展示名只在**有图标**且区域高度 ≥ 64 时显示（05-ui-design-system.md §4.5.1）：
        它是压在图标上的黑色字幕条；没有图标时格子里是居中的 `code` 文字，
        再压一条同位置的展示名只会互相遮挡。
      */}
      {imageUrl && shelf.label && rect.height >= 64 && (
        <span className="absolute inset-x-0 bottom-0 bg-black/60 px-2 py-1 text-xs font-bold text-white">
          {shelf.label}
        </span>
      )}

      {shelf.soldOut && (
        <span className="absolute inset-0 flex items-center justify-center bg-white/60 text-sm font-bold text-gray-500">
          售罄
        </span>
      )}
    </button>
  );
}

/**
 * 货架网格：坐标底板 + 可合并矩形货架 + 空洞。
 *
 * 与基线 `shop/ShelfGrid.tsx` 的差异：网格底只画坐标线，货架按 `shelfRect`
 * 绝对定位，从而支持「一个货架占多格」。货架外壳 class 沿用基线（描边出处
 * `shop/ShelfGrid.tsx:26`），仅把 `relative` 改为 `absolute`。
 */
export default function ShelfGrid({
  rows,
  cols,
  shelves,
  mode = "browse",
  rowLabels,
  onSelectShelf,
  mergeSelection,
  className = "",
}: ShelfGridProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [metrics, setMetrics] = useState(() =>
    computeGridMetrics({ containerWidth: 0, containerHeight: 0, rows, cols }),
  );

  // 用 `useLayoutEffect` 而不是 `useEffect`：测量要发生在浏览器绘制之前，
  // 否则第一帧渲染的是 `cellSize: 0`（不渲染网格），测量结果落地后才有网格——
  // 也就是「打开页面先闪一下空白」。`useLayoutEffect` 在 DOM 就绪后、绘制前同步跑完，
  // 这一帧就已经是量好的尺寸。
  //
  // SSR 影响：`useLayoutEffect` 在服务端渲染时会警告（服务端没有布局可测）。
  // 本包目前只被 Vite 客户端应用消费（apps/tenant、apps/shop 都没有 SSR 入口），
  // 所以直接用；**将来若出现 SSR 消费者**，这里要换成
  // `typeof window === 'undefined' ? useEffect : useLayoutEffect` 的同构别名。
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;

    const compute = () => {
      const rect = el.getBoundingClientRect();
      if (rect.width <= 0 || rect.height <= 0) return;
      setMetrics(
        computeGridMetrics({
          containerWidth: rect.width,
          containerHeight: rect.height,
          rows,
          cols,
        }),
      );
    };

    compute();
    const observer = new ResizeObserver(compute);
    observer.observe(el);
    return () => observer.disconnect();
  }, [rows, cols]);

  const labels = rowLabels ?? defaultRowLabels(rows);

  // 收集未分配的格子（空洞）
  const occupied = new Set<string>();
  for (const s of shelves) {
    for (let r = s.rowStart; r < s.rowStart + s.rowSpan; r++) {
      for (let c = s.colStart; c < s.colStart + s.colSpan; c++) {
        occupied.add(`${r}:${c}`);
      }
    }
  }
  const holes: Array<{ row: number; col: number }> = [];
  for (let r = 1; r <= rows; r++) {
    for (let c = 1; c <= cols; c++) {
      if (!occupied.has(`${r}:${c}`)) holes.push({ row: r, col: c });
    }
  }

  return (
    <div
      ref={ref}
      data-testid="shelf-grid"
      className={`flex h-full w-full justify-center ${className}`}
      // 居中 + 溢出 = 顶部滚不到（flex 居中会把溢出的那一半推到容器上方，
      // 而滚动只能向下）。纵向溢出时改成顶对齐，让滚动从第一行开始。
      //
      // 这里用内联样式而不是 `items-center` / `items-start` 两个 Tailwind 类：
      // 它本来就是随度量变化的动态值（和货架块的 left/top 同理），
      // 且 jsdom 里没有 Tailwind 样式表，类名读不出真实对齐——
      // 内联样式才让「溢出时不许居中」这条契约可被测试断言住。
      style={{ alignItems: metrics.overflowY ? "flex-start" : "center" }}
    >
      {metrics.cellSize > 0 && (
        <div
          data-testid="shelf-grid-scroll"
          data-overflow-x={metrics.overflowX ? "true" : "false"}
          data-overflow-y={metrics.overflowY ? "true" : "false"}
          className={[
            metrics.overflowX ? "overflow-x-auto" : "",
            metrics.overflowY ? "overflow-y-auto" : "",
          ]
            .filter(Boolean)
            .join(" ")}
          // `maxHeight: 100%` 是纵向修复的承重点：只有把滚动盒夹到容器高以内，
          // `overflow-y-auto` 才有东西可滚。否则盒子被内容撑到 616px、
          // 再被调用点的 `overflow-hidden` 裁掉——看起来和没修一样。
          style={{ maxWidth: "100%", maxHeight: "100%" }}
        >
          <div
            className="relative"
            style={{ width: metrics.contentWidth, height: metrics.contentHeight }}
          >
            {holes.map(({ row, col }) => (
              <EmptyCell
                key={`hole-${row}-${col}`}
                row={row}
                col={col}
                left={(col - 1) * (metrics.cellSize + GAP)}
                top={(row - 1) * (metrics.cellSize + GAP)}
                size={metrics.cellSize}
              />
            ))}

            {shelves.map((shelf) => (
              <ShelfBlock
                key={shelf.shelfId}
                shelf={shelf}
                cellSize={metrics.cellSize}
                mode={mode}
                onSelectShelf={onSelectShelf}
              />
            ))}

            {mergeSelection && (
              <div
                className="absolute inset-0 grid"
                style={{
                  gridTemplateColumns: `repeat(${cols}, ${metrics.cellSize}px)`,
                  gridTemplateRows: `repeat(${rows}, ${metrics.cellSize}px)`,
                  gap: GAP,
                }}
              >
                {Array.from({ length: rows * cols }, (_, i) => {
                  const row = Math.floor(i / cols) + 1;
                  const col = (i % cols) + 1;
                  const isSelected = mergeSelection.selected.some(
                    (s) => s.row === row && s.col === col,
                  );
                  return (
                    <button
                      key={`sel-${row}-${col}`}
                      type="button"
                      aria-label={`${labels[row - 1] ?? row}${col}`}
                      onClick={() => mergeSelection.onToggleCell(row, col)}
                      className={`rounded-lg border-2 ${
                        isSelected ? "border-black bg-black/20" : "border-transparent"
                      }`}
                    />
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
